import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import client from "Libs/platform";

import type { Ticket } from "@packages/client";
import type { RootState } from "Store/configureStore";

const defaultLoadFailureError = {
  message: "An error has occurred while opening a ticket"
};

export type TicketData = {
  subject: string;
  description: string;
  requester_id: string | undefined;
  priority: string | undefined;
  category: string | undefined;
  affected_url: string;
  subscription_id: string | undefined;
  attachments: { data: string | undefined; filename: string }[];
  followup_tid: string | undefined;
  branch: string;
};

export const open = createAsyncThunk(
  "tickets/open",
  async (ticket: TicketData, { rejectWithValue }) => {
    try {
      const response: Ticket = await client.openTicket(ticket);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export type OpenTicketState = {
  isLoading: boolean;
  ticket?: Ticket;
  error?: unknown;
};

export const initialState: OpenTicketState = {
  isLoading: false
};

const openTicket = createSlice({
  name: "tickets/open",
  initialState,
  reducers: {
    reset: () => initialState
  },
  extraReducers: builder => {
    builder
      .addCase(open.pending, state => {
        state.isLoading = true;
      })
      .addCase(open.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.ticket = payload;
      })
      .addCase(open.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload ?? defaultLoadFailureError;
      });
  }
});

export function selectOpenTicketIsSuccessful(state: RootState) {
  return !!state.openTicket.ticket;
}

export function selectOpenTicketIsLoading(state: RootState) {
  return state.openTicket.isLoading;
}

export function selectOpenTicketError(state: RootState) {
  return (state.openTicket.error as { message: string } | undefined)?.message;
}

export const { reset } = openTicket.actions;
export default openTicket.reducer;
